import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Edit2, Trash2 } from 'lucide-react';
import ConfirmModal from '../components/ui/ConfirmModal';
import { useToast } from '../contexts/ToastContext';
import { mockTeachers } from '../data/mockData';
import { classService } from '../services/classService';
import APIRequest from '../api/APIRequest';
import { useSelector } from 'react-redux';

const ClassView = ({ classData: propClassData }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { success, error } = useToast();
  const [classData, setClassData] = useState(propClassData);
  const [loading, setLoading] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const { user } = useSelector((state) => state.auth);


  // Fetch class data when component mounts (only if not passed as props)
  useEffect(() => {
    const fetchClassData = async () => {
      if (!id || propClassData) return;
      
      setLoading(true);
      try {
        const api = new APIRequest();
        const response = await api.request(`/admin/getClass?id=${id}`, 'GET', {}, user.token);
        
        if (response.success) {
          setClassData(response.class);
        } else {
          error(response.message || 'Failed to fetch class details');
        }
      } catch (err) {
        console.error('Error fetching class data:', err);
        error('Failed to fetch class details');
      } finally {
        setLoading(false);
      }
    };

    fetchClassData();
  }, [id, user.token, error, propClassData]);

  const handleDelete = () => {
    setIsConfirmModalOpen(true);
  };

  const confirmDelete = async () => {
    try {
      const response = await classService.deleteClass(classData?._id, user.token);
      if (response.success) {
        success('Class deleted successfully!');
        navigate('/classes');
      } else {
        error(response.message || 'Failed to delete class');
      }
    } catch (err) {
      console.error('Error deleting class:', err);
      error('Failed to delete class');
    } finally {
      setIsConfirmModalOpen(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }


  if (!classData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Class Not Found</h2>
          <p className="text-gray-600 mb-4">The class you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/classes')}
            className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
          >
            Back to Classes
          </button>
        </div>
      </div>
    );
  }

  const enrolledStudents = classData?.enrolledStudents;

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between animate-slideInDown">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/classes')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {classData?.name} - Section {classData?.section}
            </h1>
            <p className="text-gray-600 mt-1">Class details and information</p>
          </div>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => navigate(`/classes/${id}/edit`)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
          >
            <Edit2 size={16} />
            <span>Edit Class</span>
          </button>
          <button
            onClick={handleDelete}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
          >
            <Trash2 size={16} />
            <span>Delete Class</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Class Information */}
        <div className="bg-white rounded-xl shadow-sm border p-6 animate-slideInUp">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Class Information</h3>
          <div className="space-y-3">
            <div>
              <span className="text-gray-600">Class Name:</span>
              <p className="font-medium">{classData?.name}</p>
            </div>
            <div>
              <span className="text-gray-600">Section:</span>
              <p className="font-medium">{classData?.section}</p>
            </div>
            <div>
              <span className="text-gray-600">Capacity:</span>
              <p className="font-medium">{classData?.class_capacity} students</p>
            </div>
            <div>
              <span className="text-gray-600">Enrolled:</span>
              <p className="font-medium">{classData?.enrolledStudents?.length || 0} students</p>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-3 mt-2">
              <div 
                className="bg-blue-500 h-3 rounded-full transition-all shadow-sm"
                  style={{ width: `${((classData?.enrolledStudents?.length || 0) / classData?.class_capacity) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Academic Year Information */}
        <div className="bg-white rounded-xl shadow-sm border p-6 animate-slideInUp">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Academic Year</h3>
          {classData?.academicYearFrom && classData?.academicYearTo ? (
            <div className="space-y-3">
              <div>
                <p className="font-bold text-lg text-blue-600">
                  {new Date(classData.academicYearFrom).toLocaleDateString('en-GB')} 
                  {' - '}
                  {new Date(classData.academicYearTo).toLocaleDateString('en-GB')}
                </p>
              </div>
              <div className="bg-blue-50/50 border border-blue-100 rounded-2xl p-4 mt-4">
                <div className="flex justify-between items-center text-sm">
                  <div>
                    <span className="text-blue-700 font-bold uppercase text-[10px] tracking-widest">From:</span>
                    <span className="text-slate-700 ml-2 font-bold">
                      {new Date(classData.academicYearFrom).toLocaleDateString('en-GB')}
                    </span>
                  </div>
                </div>
                <div className="flex justify-between items-center text-sm mt-3">
                  <div>
                    <span className="text-blue-700 font-bold uppercase text-[10px] tracking-widest">To:</span>
                    <span className="text-slate-700 ml-2 font-bold">
                      {new Date(classData.academicYearTo).toLocaleDateString('en-GB')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-gray-500">No academic year information available</p>
            </div>
          )}
        </div>

        {/* Fees Information */}
        <div className="bg-white rounded-xl shadow-sm border p-6 animate-slideInUp">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Fees Information</h3>
          {classData?.feesPerAcademicYear || classData?.termFees ? (
            <div className="space-y-3">
              <div>
                <span className="text-gray-600">Annual Fees:</span>
                <p className="font-medium text-lg text-green-600">
                  ₹{(classData.termFees ? 
                    classData.termFees.reduce((sum, term) => sum + (term.amount || 0), 0) : 
                    classData.feesPerAcademicYear || 0
                  ).toLocaleString()}
                </p>
              </div>
              <div>
                <span className="text-gray-600">Number of Terms:</span>
                <p className="font-medium">{classData.numberOfTerms || 1} terms</p>
              </div>
              {classData?.termFees && classData.termFees.length > 0 ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
                  <h4 className="text-sm font-medium text-blue-800 mb-2">Term-wise Fee Breakdown</h4>
                  <div className="space-y-2">
                    {classData.termFees.map((termFee, index) => (
                      <div key={index} className="flex justify-between items-center text-sm">
                        <div>
                          <span className="text-blue-700">Term {index + 1}:</span>
                          {termFee.dueDate && (
                            <span className="text-gray-500 ml-2">(Due: {new Date(termFee.dueDate).toLocaleDateString()})</span>
                          )}
                        </div>
                        <span className="font-medium text-blue-800">₹{(termFee.amount || 0).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div>
                  <span className="text-gray-600">Fee per Term:</span>
                  <p className="font-medium text-blue-600">
                    ₹{Math.round((classData.feesPerAcademicYear / (classData.numberOfTerms || 1)) * 100) / 100}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-gray-500">No fee information available</p>
            </div>
          )}
        </div>

        {/* Class Incharge */}
        <div className="bg-white rounded-xl shadow-sm border p-6 animate-slideInUp">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Class Incharge</h3>
          {classData?.classIncharge ? (
            <div 
              onClick={() => navigate(`/staff/${classData.classIncharge._id}`)}
              className="flex items-center space-x-3 cursor-pointer p-2 hover:bg-slate-50 rounded-2xl transition-all group"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center overflow-hidden border-2 border-transparent group-hover:border-blue-200 transition-all">
                {classData?.classIncharge?.image ? (
                  <img 
                    src={`/${classData.classIncharge.image}`} 
                    alt={classData.classIncharge.name} 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className={`w-full h-full bg-blue-100 flex items-center justify-center ${classData?.classIncharge?.image ? 'hidden' : ''}`}>
                  <span className="text-blue-600 font-semibold">
                    {classData?.classIncharge?.name?.split(' ').map(n => n?.[0]).join('').slice(0, 2)}
                  </span>
                </div>
              </div>
              <div className="flex-1">
                <p className="font-bold text-slate-900 group-hover:text-blue-600 transition-colors">{classData?.classIncharge?.name}</p>
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">ID: {classData?.classIncharge?.employeeId || 'N/A'}</p>
                <div className="mt-2 space-y-1">
                  <p className="text-xs text-slate-600 font-medium bg-slate-100 px-2 py-0.5 rounded-lg inline-block">{classData?.classIncharge?.qualification}</p>
                  <p className="text-[11px] text-blue-500 font-bold flex items-center gap-1">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    {classData?.classIncharge?.phone}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-gray-500">No incharge assigned</p>
          )}
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-sm border p-6 animate-slideInUp">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions</h3>
          <div className="space-y-3">
            <button
              onClick={() => navigate(`/classes/${classData?._id}/edit`)}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
            >
              <Edit2 size={16} />
              <span>Edit Class</span>
            </button>
            <button
              onClick={handleDelete}
              className="w-full bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center space-x-2"
            >
              <Trash2 size={16} />
              <span>Delete Class</span>
            </button>
          </div>
        </div>
      </div>

      {/* Students and Teachers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Enrolled Students */}
        <div className="bg-white rounded-xl shadow-sm border p-6 animate-slideInUp">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Enrolled Students</h3>
          <div className="space-y-3 max-h-60 overflow-y-auto">
            {enrolledStudents.length > 0 ? (
              enrolledStudents.map(student => (
                <div 
                  key={student._id || student.id} 
                  onClick={() => navigate(`/students/${student._id || student.id}`)}
                  className="flex items-center space-x-3 p-3 bg-slate-50/50 hover:bg-slate-100 rounded-2xl cursor-pointer transition-all group"
                >
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center overflow-hidden border-2 border-transparent group-hover:border-blue-200 transition-all">
                    {student.image ? (
                      <img 
                        src={`/${student.image}`} 
                        alt={student.name} 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className={`w-full h-full bg-blue-100 flex items-center justify-center ${student.image ? 'hidden' : ''}`}>
                      <span className="text-blue-600 font-semibold text-sm">
                        {student?.name?.split(' ').map(n => n?.[0]).join('').slice(0, 2)}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-sm text-slate-800 group-hover:text-blue-600 transition-colors">{student?.name}</p>
                    <div className="flex items-center gap-3 mt-0.5">
                      <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">REG: {student?.regNo || 'N/A'}</p>
                      <p className="text-[10px] text-blue-500 font-bold flex items-center gap-1">
                        <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        {student?.phone}
                      </p>
                    </div>
                  </div>
                  <div className="text-blue-500 opacity-0 group-hover:opacity-100 transition-all">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-8">No students enrolled</p>
            )}
          </div>
        </div>

        {/* Allocated Teachers */}
        {/* <div className="bg-white rounded-xl shadow-sm border p-6 animate-slideInUp">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Allocated Teachers</h3>
          <div className="space-y-3 max-h-60 overflow-y-auto">
            {allocatedTeachers?.length > 0 ? (
              allocatedTeachers.map(teacher => (
                <div key={teacher.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                    <span className="text-emerald-600 font-semibold text-sm">
                      {teacher?.name?.split(' ').map(n => n?.[0]).join('').slice(0, 2)}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-sm">{teacher?.name}</p>
                    <p className="text-xs text-gray-600">{teacher?.qualification}</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                        {teacher?.subjects?.slice(0, 2).map(subject => (
                        <span key={subject.name} className="px-1 py-0.5 bg-blue-100 text-blue-800 text-xs rounded">
                          {subject?.name}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-8">No teachers allocated</p>
            )}
          </div>
        </div> */}
      </div>

      <ConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Class"
        message="Are you sure you want to delete this class? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default ClassView;
